/**
 * useReorderGallery - Mutation hook for reordering gallery images
 * Supports optimistic updates with rollback on error
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { reorderStationGallery } from '../../api/stationImages';
import { IStationImagesResponse } from '../../components/StationMedia/types';
import { STATION_IMAGES_QUERY_KEY } from './useStationImages';

interface UseReorderGalleryOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for reordering gallery images with optimistic updates
 *
 * @param stationId - The station/location UUID
 * @param options - Callback options
 * @returns Mutation object with reorder function
 *
 * @example
 * const { mutate: reorder } = useReorderGallery(stationId);
 * reorder(['image-id-1', 'image-id-2', 'image-id-3']);
 */
const useReorderGallery = (
  stationId: string,
  options?: UseReorderGalleryOptions
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string[], { previous?: IStationImagesResponse }>({
    mutationFn: async (imageIds: string[]) => {
      await reorderStationGallery(stationId, imageIds);
    },
    // Optimistic update
    onMutate: async (newOrder) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: [STATION_IMAGES_QUERY_KEY, stationId],
        });

        // Snapshot the previous value
        const previous = queryClient.getQueryData<IStationImagesResponse>([
          STATION_IMAGES_QUERY_KEY,
          stationId,
        ]);

        // Optimistically update the order
        queryClient.setQueryData<IStationImagesResponse>(
          [STATION_IMAGES_QUERY_KEY, stationId],
          (old) => {
            if (!old) return old;

            // Create a map of images by ID for quick lookup
            const imageMap = new Map(
              old.gallery.map((img) => [img.id, img])
            );

            // Reorder based on the new order
            const reorderedGallery = newOrder
              .map((id) => imageMap.get(id))
              .filter((img): img is NonNullable<typeof img> => img !== undefined);

            return {
              ...old,
              gallery: reorderedGallery,
            };
          }
        );

        return { previous };
      },

      // Rollback on error
      onError: (error, variables, context) => {
        if (context?.previous) {
          queryClient.setQueryData(
            [STATION_IMAGES_QUERY_KEY, stationId],
            context.previous
          );
        }
        options?.onError?.(error);
      },

      // Refetch after success or error
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [STATION_IMAGES_QUERY_KEY, stationId],
        });
      },

    onSuccess: () => {
      options?.onSuccess?.();
    },
  });
};

export default useReorderGallery;
